import { ErrorMessage as HookformErrorMessage } from '@hookform/error-message';
import { useRef } from 'react';
import { Controller, get, useFormContext, type FieldError } from 'react-hook-form';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage, Label } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { mergeRefs } from '#util/mergeRefs.ts';
import { PortsInput } from './PortsInput.tsx';
import { validatePorts } from './validatePorts.ts';

const FIELD_NAME = 'portRange';

type Props = {
  className?: string;
  disabled?: boolean;
};

/**
 * Collects a port or range of ports from the user
 */
export function PortsField({ className, disabled }: Props) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const portError = get(errors, FIELD_NAME) as FieldError | null;

  return (
    <Field
      label={
        <Label htmlFor="port-range">
          <span>
            Port or port range{' '}
            <HelpTip className="pad-0" portal={false}>
              <span>
                Allow a single port (22) or range of ports (21-23).
                <br />
                Specifying ”any” will allow every port (1-65,535).
              </span>
            </HelpTip>
          </span>
        </Label>
      }
      id="port-range"
      className={className}
    >
      <Controller
        control={control}
        name={FIELD_NAME}
        rules={{
          validate: () => validatePorts(inputRef.current?.value ?? ''),
        }}
        render={({ field }) => {
          return (
            <PortsInput
              {...field}
              id="port-range"
              ref={mergeRefs(inputRef, field.ref)}
              className="width-100"
              error={portError}
              disabled={disabled}
            />
          );
        }}
      />

      <HookformErrorMessage errors={errors} name={FIELD_NAME} as={ErrorMessage} className="mb-8" />
    </Field>
  );
}
